import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AuthUser, LoginCredentials, ResetPassword, UserRole } from '@shared/types';
import {
  BehaviorSubject,
  catchError,
  filter,
  finalize,
  first,
  fromEvent,
  map,
  merge,
  Observable,
  switchMap,
  take,
  tap,
  throttleTime,
  throwError
} from 'rxjs';
import { BYPASS_AUTH_TOKEN } from './auth.interceptor';
import { LanguageService } from './language.service';
import { UtilsService } from './utils.service';

const ACTIVITY_KEY = 'lastActivityAt';
const INACTIVITY_WINDOW_MS = 30 * 60 * 1000; // 30 minutes
const PROFILE_KEY = 'user_profile';
const TOKEN_KEY = 'user_tokens';

/**
 * DXCAuthService is responsible for managing authentication state and user information.
 * It interacts with the backend for login and logout operations and maintains the user's
 * session information in local storage and a reactive signal.
 */
@Injectable({ providedIn: 'root' })
export class DXCAuthService {
  private httpClient = inject(HttpClient);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private jwtHelper = inject(JwtHelperService);
  private utilsService = inject(UtilsService);
  private languageService = inject(LanguageService);
  private document = inject(DOCUMENT);

  // A reactive signal to store and observe changes in the current user's state.
  public userSignal: WritableSignal<AuthUser | null> = signal<AuthUser | null>(
    null
  );

  // Token refresh state — shared across concurrent requests
  private refreshing = false;
  private refreshSubject = new BehaviorSubject<string | null>(null);

  constructor() {
    this.languageService.init();
    const user = this.getUser(); // Restore user signal from storage on app init / hard refresh
    if (user) this.languageService.applyUserLanguage(user.language);
    this.initActivityTracking();
  }

  /**
   * Listens to user input events and writes a timestamp to localStorage so the
   * interceptor can decide whether inactivity has exceeded the timeout window.
   * Throttled to at most once every 30 seconds to avoid excessive writes.
   */
  private initActivityTracking(): void {
    const events = ['click', 'keydown', 'mousemove', 'scroll', 'touchstart'];
    merge(...events.map((e) => fromEvent(this.document, e)))
      .pipe(throttleTime(30_000))
      .subscribe(() => {
        localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
      });

    // Record activity immediately on init so a fresh login isn't treated as inactive
    localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
  }

  /**
   * Returns true if the user has interacted with the app within the inactivity window.
   */
  public isRecentlyActive(): boolean {
    const stored = localStorage.getItem(ACTIVITY_KEY);
    if (!stored) return false;
    return Date.now() - parseInt(stored, 10) < INACTIVITY_WINDOW_MS;
  }

  /**
   * Initiates the login process by submitting the provided login credentials to the server.
   * Upon successful authentication, it stores the user's session data and redirects to either a specified return URL or the home page.
   * @param loginCredentials The credentials used for logging in, including username and password.
   * @returns An Observable emitting the authenticated user's data upon successful login.
   */
  public login(loginCredentials: LoginCredentials): Observable<AuthUser> {
    return this.httpClient
      .post<AuthUser>(
        this.utilsService.buildApiUrl('token/login/'),
        loginCredentials,
        { context: new HttpContext().set(BYPASS_AUTH_TOKEN, true) }
      )
      .pipe(
        tap((res) => {
          this.activatedRoute.queryParams.pipe(first()).subscribe((params) => {
            const returnUrl = params['returnUrl'] || '/';

            const token = res.access || '';

            this.setUser({ ...res, token, email: loginCredentials.email });

            // decode the token to get the user id and use it to get the user detail for the SCB
            const decodedToken = this.jwtHelper.decodeToken(token) as {
              user_id?: number | string;
            };
            const userId = decodedToken?.user_id;

            this.getLoggedInUserDetail(userId).subscribe((userResult) => {
              this.setUser({ ...this.userSignal(), ...userResult });
              this.languageService.applyUserLanguage(userResult.language);
              const decoded = decodeURIComponent(returnUrl);
              const safeUrl = decoded.startsWith('/') && !decoded.startsWith('//') ? decoded : '/';
              this.router.navigateByUrl(safeUrl);
            });
          });
        })
      );
  }

  private getLoggedInUserDetail(
    userId?: number | string
  ): Observable<AuthUser> {
    const endpoint = userId
      ? `users/user_detail/${userId}`
      : 'users/user_detail/';
    return this.httpClient.get<AuthUser>(
      this.utilsService.buildApiUrl(endpoint)
    );
  }

  /**
   * Persists the user session. Non-sensitive profile fields go to localStorage
   * (survives tab close); tokens go to sessionStorage (cleared when tab closes,
   * never written to disk by the browser as a persistent record).
   */
  public setUser(user: AuthUser) {
    const { token, access, refresh, ...profile } = user;
    sessionStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
    sessionStorage.setItem(TOKEN_KEY, JSON.stringify({ token, access, refresh }));
    this.userSignal.set(user);
  }

  /**
   * Reconstructs the full AuthUser from the signal, or from split storage on
   * page reload. Also migrates any session that was stored in the old 'user'
   * localStorage key.
   */
  private getUser(): AuthUser | null {
    let user = this.userSignal();
    if (!user) {
      const profileStr = sessionStorage.getItem(PROFILE_KEY);
      const tokensStr = sessionStorage.getItem(TOKEN_KEY);

      if (profileStr && tokensStr) {
        user = { ...JSON.parse(profileStr), ...JSON.parse(tokensStr) };
        this.userSignal.set(user);
      } else {
        // One-time migration from old storage format
        const legacy = localStorage.getItem('user');
        if (legacy) {
          localStorage.removeItem('user');
          this.setUser(JSON.parse(legacy) as AuthUser);
          user = this.userSignal();
        }
      }
    }
    return user;
  }

  /**
   * Retrieves the current user's authentication token.
   * @returns The authentication token or null if not logged in.
   */
  public getAuthToken(): string | null {
    const tokensStr = sessionStorage.getItem(TOKEN_KEY);
    if (!tokensStr) return null;
    const tokens = JSON.parse(tokensStr) as { token?: string; access?: string };
    return tokens.token || tokens.access || null;
  }

  /**
   * Retrieves the stored refresh token.
   */
  public getRefreshToken(): string | null {
    const tokensStr = sessionStorage.getItem(TOKEN_KEY);
    if (!tokensStr) return null;
    return (JSON.parse(tokensStr) as { refresh?: string }).refresh || null;
  }

  /**
   * Updates the stored access token (and optionally refresh token) without
   * touching other user fields.
   */
  public updateAccessToken(access: string, refresh?: string): void {
    const tokensStr = sessionStorage.getItem(TOKEN_KEY);
    if (!tokensStr) return;
    const tokens = JSON.parse(tokensStr);
    const updated = { ...tokens, token: access, access, ...(refresh ? { refresh } : {}) };
    sessionStorage.setItem(TOKEN_KEY, JSON.stringify(updated));
    const user = this.userSignal();
    if (user) {
      this.userSignal.set({ ...user, token: access, access, ...(refresh ? { refresh } : {}) });
    }
  }

  /**
   * Calls the token refresh endpoint and updates stored tokens.
   * Concurrent callers queue behind the first in-flight refresh.
   */
  public refreshAccessToken(): Observable<string> {
    if (this.refreshing) {
      // Another request is already refreshing — wait for it to complete
      return this.refreshSubject.pipe(
        filter((token): token is string => token !== null),
        take(1)
      );
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    this.refreshing = true;
    this.refreshSubject.next(null);

    return this.httpClient
      .post<{ access: string; refresh?: string }>(
        this.utilsService.buildApiUrl('token/refresh/'),
        { refresh: refreshToken },
        { context: new HttpContext().set(BYPASS_AUTH_TOKEN, true) }
      )
      .pipe(
        tap((res: { access: string; refresh?: string }) => {
          this.updateAccessToken(res.access, res.refresh);
          this.refreshing = false;
          this.refreshSubject.next(res.access);
        }),
        map((res: { access: string; refresh?: string }) => res.access),
        catchError((err: unknown) => {
          this.refreshing = false;
          this.refreshSubject.next(null);
          return throwError(() => err);
        })
      );
  }

  /**
   * Checks if the user is currently logged in based on the presence and validity of the auth token.
   * @returns true if the user is logged in, false otherwise.
   */
  public isLoggedIn(): boolean {
    const token = this.getAuthToken();
    return token ? !this.jwtHelper.isTokenExpired(token) : false;
  }

  /**
   * Performs the logout operation by clearing the user's session information and navigating to the auth page.
   * @returns An Observable with the logout response from the backend.
   */
  logout(): Observable<{ detail: string }> {
    const refreshToken = this.getRefreshToken();
    return this.httpClient
      .post<{ detail: string }>(
        // Note: plural "users/", unlike forgot_password/reset_password/photo_upload
        // which live under singular "user/" — logout is registered in users.urls
        // alongside list/create, not in backend/urls.py with the others.
        this.utilsService.buildApiUrl('users/logout/'),
        refreshToken ? { refresh: refreshToken } : {}
      )
      .pipe(
        finalize(() => {
          this.clearSessionAndNavigateToAuth(this.router);
        })
      );
  }

  /**
   * Initiates a password reset process by sending the user's email to the backend.
   * On success, the backend sends a password reset link to the provided email.
   * @param email The user's email address.
   * @returns An Observable with a message indicating the result of the operation.
   */
  public forgotPassword(email: {
    email: string;
  }): Observable<{ detail: string }> {
    return this.httpClient.post<{ detail: string }>(
      this.utilsService.buildApiUrl('user/forgot_password/'),
      email,
      { context: new HttpContext().set(BYPASS_AUTH_TOKEN, true) }
    );
  }

  /**
   * Resets the users password by submitting a request with the user's ID, token, and new password.
   * @param resetPassword An object containing the user's ID, token, and new password.
   * @returns An Observable with a message indicating the result of the operation.
   */
  public resetPassword(
    resetPassword: ResetPassword
  ): Observable<{ detail?: string }> {
    return this.httpClient.post<{ detail?: string }>(
      this.utilsService.buildApiUrl('user/reset_password/'),
      resetPassword,
      { context: new HttpContext().set(BYPASS_AUTH_TOKEN, true) }
    );
  }

  /**
   * Clears the user's session information from local storage and the userSignal, then navigates to the auth page.
   * @param router The Router instance used for navigation.
   * @param targetUrl Explicit URL to use as the returnUrl. Pass state.url from a route guard to
   *   capture the correct destination during initial navigation (hard refresh), when
   *   router.routerState.snapshot.url may not yet reflect the navigated-to URL.
   */
  public clearSessionAndNavigateToAuth(router: Router, targetUrl?: string) {
    this.userSignal.set(null);
    sessionStorage.removeItem(PROFILE_KEY);
    localStorage.removeItem(ACTIVITY_KEY);
    localStorage.removeItem('user'); // clean up legacy key if present
    sessionStorage.removeItem(TOKEN_KEY);
    const currentUrl = targetUrl ?? router.routerState.snapshot.url;
    const isLoginPage = currentUrl.startsWith('/login');
    const returnUrlParams =
      currentUrl && currentUrl !== '/' && !isLoginPage
        ? { queryParams: { returnUrl: currentUrl } }
        : {};
    return router.navigate(['login'], returnUrlParams);
  }

  /**
   * Returns the provider's OAuth2 authorization URL for the frontend to redirect to.
   */
  public getSSOAuthUrl(provider: string, redirectUri: string): Observable<{ url: string; state: string }> {
    return this.httpClient.get<{ url: string; state: string }>(
      this.utilsService.buildApiUrl(`auth/sso/${provider}/url/`),
      {
        params: { redirect_uri: redirectUri },
        context: new HttpContext().set(BYPASS_AUTH_TOKEN, true)
      }
    );
  }

  /**
   * Exchanges an SSO authorization code for a JWT pair, stores the session, and navigates home.
   */
  public loginWithSSOCode(provider: string, code: string, redirectUri: string): Observable<void> {
    return this.httpClient
      .post<{ access: string; refresh: string }>(
        this.utilsService.buildApiUrl(`auth/sso/${provider}/callback/`),
        { code, redirect_uri: redirectUri },
        { context: new HttpContext().set(BYPASS_AUTH_TOKEN, true) }
      )
      .pipe(
        switchMap((res) => {
          this.setUser({ ...res, token: res.access } as AuthUser);
          const decoded = this.jwtHelper.decodeToken(res.access) as { user_id?: number | string };
          return this.getLoggedInUserDetail(decoded?.user_id).pipe(
            tap((userResult) => {
              this.setUser({ ...this.userSignal(), ...userResult } as AuthUser);
              this.languageService.applyUserLanguage(userResult.language);
              this.router.navigateByUrl('/');
            })
          );
        }),
        map(() => void 0)
      );
  }

  public hasPermissions(permissions: string[]): boolean {
    const user = this.getUser();

    if (user && permissions.length > 0) {
      return permissions.every((permission) =>
        (user.permissions ?? []).includes(permission)
      );
    }

    return false;
  }

  /**
   * True if the current user's role (the 2-letter code in AuthUser.user_group,
   * e.g. 'AD') is one of `roles`. This is the real authorization gate on the
   * frontend now — hasPermissions() is legacy and being phased out in favor
   * of this, since the backend itself authorizes by role rather than by
   * granular Django permission codenames.
   */
  public hasRole(...roles: (keyof typeof UserRole)[]): boolean {
    const user = this.getUser();
    return !!user && roles.includes(user.user_group as keyof typeof UserRole);
  }
}
